import { useInView } from "react-intersection-observer";
import { useLocation, useNavigate } from "react-router";
import { useBtSdkAdsCanaryConfigContext } from "@components/Advertisement/BtSdkAdsCanaryConfigContext";
import useSendDataToGTM from "@features/analytics/hooks/useSendDataToGTM";
import { getGAData } from "@routes/Article/utils/helpers";
import { FeedArticleData } from "@routes/Article/utils/types";
import { getEnvironment } from "@util/constant";
import { getEnvironmentDomain } from "@util/helpers";
import { getBtSdkAdsVirtualArticleNavigationState } from "@util/sdkAdsConfig";
import posthog from "posthog-js";

export const useHookUpdateUrlArticle = (
  urlPath: string,
  isNavigate: boolean,
  article: FeedArticleData,
  isChinese: boolean,
  variant?: string,
  setArticleObj?: (article: FeedArticleData) => void
) => {
  const sendDataToGTM = useSendDataToGTM();
  const navigate = useNavigate();
  const sdkAdsConfig = useBtSdkAdsCanaryConfigContext();
  const { search, state } = useLocation();
  const newUrl = urlPath + search;

  const { ref } = useInView({
    threshold: 0.01,
    onChange: (inView) => {
      if (typeof window === "undefined") return;
      if (!inView || !isNavigate || !article?.id) return;

      if (typeof setArticleObj === "function") setArticleObj(article);

      // Only tag this virtual (infinite-scroll) navigation while a web2-ads SDK
      // canary session is active (config present — either the canary article
      // itself or a sibling kept alive by the latch), so the latch survives the
      // scroll onto sibling articles. Legacy (non-canary) article navigations
      // are left exactly as before — no extra router state.
      navigate(newUrl, {
        preventScrollReset: true,
        ...(sdkAdsConfig
          ? { state: getBtSdkAdsVirtualArticleNavigationState(state) }
          : {}),
      });

      sendDataToGTM({
        ...window._data,
        ...getGAData(article, isChinese),
        abVariant: variant || "control",
        virtual_url: newUrl,
        event: "virtual_pv",
      });

      // For PostHog tracking
      const currentUrl = getEnvironmentDomain(getEnvironment()) + newUrl;
      posthog.capture("$pageview", {
        $pathname: newUrl,
        $current_url: currentUrl,
      });
    },
  });

  return { ref };
};
